import type { ReactNode } from 'react';
import { iconAt, type IconKey } from './raster';

/**
 * A UI icon: the Codex raster (`icons/<name>`, one edition for both themes) when it exists and still reads at `size`
 * CSS pixels (ICON_MIN_PX); below that its simplified small edition (`icons/<name>-small`) if Codex drew one;
 * otherwise `fallback` — the spot's own vector glyph. Always decorative: the control that holds it carries the
 * accessible name.
 */
export function UiIcon({
  name,
  size,
  fallback,
  together,
  className,
}: {
  name: IconKey;
  size: number;
  fallback: ReactNode;
  /**
   * icons that sit side by side (a tab bar, a pair of buttons): raster only if every one of them has a raster at
   * `size` — its own or its small edition; one missing → the whole set keeps its glyphs
   */
  together?: readonly IconKey[];
  className?: string;
}) {
  const src = iconAt(name, size);
  if (!src || (together && !together.every((k) => iconAt(k, size)))) return <>{fallback}</>;
  return (
    <img
      className={className}
      src={src}
      width={size}
      height={size}
      alt=""
      aria-hidden="true"
      draggable={false}
      data-icon={name}
      style={{ display: 'inline-block', flex: 'none', verticalAlign: 'middle', objectFit: 'contain' }}
    />
  );
}
